import React, { useEffect, useState } from "react";
import axios from "axios";

function GrnSubmission() {
  // 🔹 Vendor + PO Code State
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [vendorCode, setVendorCode] = useState("");
  const [poCodes, setPoCodes] = useState([]);
  const [selectedPo, setSelectedPo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [poType, setPoType] = useState("");

  // 🔹 Download GRN State
  const [downloadPoCodes, setDownloadPoCodes] = useState([]);
  const [selectedDownloadPo, setSelectedDownloadPo] = useState("");

  // 🔹 Upload GRN State
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMsg, setUploadMsg] = useState("");
  const [uploadLoading, setUploadLoading] = useState(false);

  // 🔹 Invoice State
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceMsg, setInvoiceMsg] = useState("");
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [selectedGrnFile, setSelectedGrnFile] = useState(null);

  // Fetch vendors on load
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api?type=vendors`)
      .then((res) => {
        console.log("Vendors API Response:", res.data);
        setVendors(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => console.error("Error fetching vendors:", err));
  }, []);

  // Handle vendor change
  const handleVendorChange = (e) => {
    const companyName = e.target.value;
    setSelectedVendor(companyName);

    const vendor = vendors.find((v) => v.companyName === companyName);
    const code = vendor ? vendor.vendorCode : "";
    setVendorCode(code);

    if (code) fetchPoCodes(code);
    else setPoCodes([]);

    setSelectedPo(null);
  };

  // Fetch PO codes by vendor
  const fetchPoCodes = async (vendorCode) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api?type=vendor&vendorCode=${vendorCode}`
      );
      const data = await response.json();
      console.log("fetchPoCodes response:", data);

      // ✅ Ensure we always set an array
      if (Array.isArray(data)) {
        setPoCodes(data);
      } else if (data && Array.isArray(data.poCodes)) {
        setPoCodes(data.poCodes);
      } else if (data && typeof data === "object") {
        setPoCodes(Object.values(data)); // fallback if it's an object
      } else {
        setPoCodes([]);
      }
    } catch (err) {
      console.error("Error fetching PO codes:", err);
      setPoCodes([]); // ✅ fallback to empty array
    }
    setLoading(false);
  };

  // Fetch PO codes for download (on mount)
  useEffect(() => {
    const fetchDownloadPoCodes = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api?type=po-codes`
        );
        const data = await response.json();
        console.log("PO Codes API Response:", data);
        setDownloadPoCodes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching PO codes for download:", err);
        setDownloadPoCodes([]);
      }
    };
    fetchDownloadPoCodes();
  }, []);

  // Fetch PO Type when selected PO changes
  useEffect(() => {
    const poCode = selectedPo || selectedDownloadPo;
    if (!poCode) return;

    fetch(`${process.env.REACT_APP_API_URL}/api?type=get-po-info&poCode=${poCode}`)
      .then(res => res.json())
      .then(data => setPoType(data.poType));
  }, [selectedPo, selectedDownloadPo]);

  // Handle GRN download
  const handleDownload = async () => {
    if (!selectedDownloadPo) {
      alert("Please select a PO code before downloading.");
      return;
    }

    if (loading) return;

    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api?type=download-grn&poCode=${selectedDownloadPo}`
      );

      if (!response.ok) throw new Error("Failed to download GRN sheet");

      // 🔹 Extract filename from response headers
    const disposition = response.headers.get("Content-Disposition");
    let fileName = `GRN-${selectedDownloadPo}.xlsx`;

    if (disposition && disposition.includes("filename=")) {
      fileName = disposition
        .split("filename=")[1]
        .replace(/"/g, "")
        .trim();
    }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `GRN-${selectedDownloadPo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("GRN Download Error:", err);
      alert("Failed to download GRN: " + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle GRN upload
  const handleUploadGrn = async () => {
    if (!uploadFile) {
      setUploadMsg("Please select a file to upload.");
      return;
    }

    if (!selectedDownloadPo && !selectedPo) {
      setUploadMsg("Please select a PO Code before uploading GRN.");
      return;
    }

    setUploadLoading(true);
    setUploadMsg("");

    try {
      const formData = new FormData();
      formData.append("grnFile", uploadFile);
      formData.append("poCode", selectedDownloadPo || selectedPo);
      formData.append("invoiceNumber", invoiceNumber || null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/post/upload-grn`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to upload GRN sheet");

      let msg = `✅ Success: ${data.message} (${data.updatedRows} rows updated)`;

      // If GRN was already updated → show skipped SKUs
      if (Array.isArray(data.skippedSkus) && data.skippedSkus.length > 0) {
        msg += `\n\nSkipped SKUs:\n- ${data.skippedSkus.join("\n- ")}`;
      }

      setUploadMsg(msg);
      setUploadFile(null);
      document.getElementById("uploadInput").value = "";
    } catch (err) {
      setUploadMsg(`❌ Error: ${err.message}`);
    } finally {
      setUploadLoading(false);
    }
  };

  // Set today's date on first load
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setInvoiceDate(today);
  }, []);

  //  Handle Invoice Submission (with GRN + reset date correctly)
  const handleInvoiceSubmit = async () => {
    const poCodeToUse = selectedPo || selectedDownloadPo;

    if (
      !invoiceFile ||
      !invoiceDate ||
      !poCodeToUse ||
      !selectedVendor ||
      !vendorCode ||
      !selectedGrnFile
    ) {
      setInvoiceMsg(
        "Please select Vendor, Vendor Code, PO Code, Invoice Date, GRN File and Invoice File."
      );
      return;
    }

    setInvoiceLoading(true);
    setInvoiceMsg("");

    try {
      const formData = new FormData();
      formData.append("grnFile", selectedGrnFile);
      formData.append("invoiceFile", invoiceFile);
      formData.append("invoiceDate", invoiceDate);
      formData.append("vendor", selectedVendor);
      formData.append("vendorCode", vendorCode);
      formData.append("poCode", poCodeToUse);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/post/upload-invoice`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();

      // Check Duplicate Invoice Submission
      if (
        response.status === 400 &&
        data.message.includes("already submitted")
      ) {
        setInvoiceMsg(`
    <div 
      style="
        margin-top:20px;
        background:#424141; 
        padding:20px; 
        color:#fff; 
        border-radius:10px;
        text-align:center;
        display:flex;
        flex-direction:column;
        align-items:center;
        justify-content:center;
        font-size:16px;
        line-height:1.5;
      "
    >
      <strong>❌ Invoice Already Submitted</strong>
      ${data.message}
    </div>
  `);
        return;
      }

      if (!response.ok)
        throw new Error(data.message || "Failed to send invoice");

      // Detailed message
      const formattedReminderDate = new Date(data.reminderDate).toLocaleString(
        "en-IN",
        {
          weekday: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      );

      setInvoiceMsg(`
        <div style="
          background:#424141;
          color:#f8f8f8;
          border-radius:10px;
          padding:15px;
          text-align:left;
          font-size:16px;
          margin:15px auto;
        ">
          ✅ <strong>Success:</strong> ${data.message}<br />
          🏷️ <strong>PO Code:</strong> ${poCodeToUse}<br />
          ${
            data.poType === "LSL" && data.invoiceNumber
              ? `📄 <strong>Invoice Number:</strong> ${data.invoiceNumber}<br />`
              : ""
          }
          🏢 <strong>Vendor:</strong> ${selectedVendor}<br />
          ⏳ <strong>Reminder Days:</strong> ${data.reminderDays}<br />
          📅 <strong>Reminder scheduled for:</strong> ${formattedReminderDate}
        </div>
      `);

      // Reset fields
      setSelectedGrnFile(null);
      setInvoiceFile("");
      setInvoiceDate(new Date().toISOString().split("T")[0]);
      setSelectedVendor("");
      setVendorCode("");
      setSelectedPo("");
      setSelectedDownloadPo("");
      document.getElementById("invoiceInput").value = "";
    } catch (err) {
      setInvoiceMsg(`❌ Error: ${err.message}`);
    } finally {
      setInvoiceLoading(false);
    }
  };

  return (
    <div className="sanchit_GRN">
      <div className="form-container" style={{ padding: "20px" }}>
        {/* Vendor Selection */}
        <div
          style={{
            display: "inline-flex",
            gap: "20px",
            alignItems: "center",
            width: "100%",
          }}
        >
          <label>
            <h3>Company Name:</h3>
            <select
              className="large-input"
              value={selectedVendor}
              onChange={handleVendorChange}
            >
              <option value="">-- Choose Vendor --</option>
              {vendors.map((vendor, index) => (
                <option key={index} value={vendor.companyName}>
                  {vendor.companyName}
                </option>
              ))}
            </select>
          </label>
          <label>
            <h3>Vendor Code:</h3>
            <input
              placeholder="-- Vendor Code --"
              className="vendor-input large-input"
              type="text"
              value={vendorCode}
              readOnly
            />
          </label>
        </div>

        <br />

        {/* Download GRN */}
        <div
          style={{
            display: "inline-flex",
            gap: "20px",
            alignItems: "center",
            width: "100%",
          }}
        >
          <select
            className="large-input"
            value={selectedDownloadPo}
            onChange={(e) => setSelectedDownloadPo(e.target.value)}
            disabled={downloadPoCodes.length === 0 || loading}
            
          >
            <option value="">-- Select PO Code --</option>
            {downloadPoCodes.map((po) => (
              <option key={po.poCode} value={po.poCode}>
                {po.poCode}_Created at{" "}
                {new Date(po.createdAt).toLocaleDateString()})
              </option>
            ))}
          </select>
          <label style={{
              display: 'flex', 
              gap: '20px', 
              alignItems: 'center',
              width: '50%',
              }}
            > 
            <input
              placeholder="-- PO Type --"
              className="po-type-input"
              type="text"
              value={poType}
              readOnly
              style={{ maxWidth: "34%",}}
            />
        
          <button
            onClick={handleDownload}
            disabled={loading || !selectedDownloadPo}
            style={{ maxWidth: "51%" }}
          >
            {loading ? "Downloading..." : "Download GRN Sheet"}
          </button>
          </label>
        </div>

        <br />

        {/* Upload GRN */}
        <div
          style={{
            display: "inline-flex",
            gap: "20px",
            alignItems: "center",
            width: "100%",
          }}
        >
          <input
            id="uploadInput"
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setUploadFile(e.target.files[0])}
            disabled={uploadLoading}
          />
            <input
              type="text"
              placeholder="Invoice Number"
              value={invoiceNumber}
              required
              onChange={(e) => setInvoiceNumber(e.target.value)}
              className="invoice-number-input"
            />
          <button
            onClick={handleUploadGrn}
            disabled={uploadLoading || !uploadFile}
          >
            {uploadLoading ? "Uploading..." : "Upload GRN"}
          </button>
        </div>
        {/* {uploadMsg && <p>{uploadMsg}</p>} */}
        <div style={{ display: "flex" }}>
          {uploadMsg && (
            <div className="grn-message-row">
              <p
                className={
                  uploadMsg.includes("Success") ? "success-msg" : "error-msg"
                }
              >
                {uploadMsg}
              </p>
            </div>
          )}
        </div>

        {/* Submit Invoice */}
        <div>
          <div
            style={{
              display: "flex",
              gap: 20,
              alignItems: "center",
              color: "#1c1c1c",
            }}
          >
            <input
              style={{ width: "20%", color: "#1c1c1c", caretColor: "#1c1c1c" }}
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              required
            />

            {/* <div className="file-upload-field">
         <label htmlFor="invoiceFile">Invoice File</label> */}
            <input
              style={{ width: "30%" }}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setSelectedGrnFile(e.target.files[0])}
            />
            {/* </div> */}

            {/* <div className="file-upload-field">
          <label htmlFor="grnFile">GRN File</label> */}
            <input
              style={{ width: "30%" }}
              placeholder=""
              id="invoiceInput"
              type="file"
              accept=".pdf,.docx,.xlsx,.xls"
              onChange={(e) => setInvoiceFile(e.target.files[0])}
            />
            {/* </div> */}

            <button
              style={{ width: "20%", height: "45px" }}
              onClick={handleInvoiceSubmit}
              disabled={invoiceLoading}
            >
              {invoiceLoading ? "Submitting..." : "Submit Invoice"}
            </button>
          </div>
          {invoiceMsg && (
            <div dangerouslySetInnerHTML={{ __html: invoiceMsg }} />
          )}
        </div>
      </div>
    </div>
  );
}

export default GrnSubmission;
